/**
 * Makes the first-login password set (Cognito NEW_PASSWORD_REQUIRED) safe to retry.
 *
 * Cognito's challenge session can be used once. When the request reaches Cognito
 * but its response is lost in transit, the password IS changed while the browser
 * sees "Network error", and Amplify UI keeps the form open. Every retry then fails
 * with "session can only be used once", and nothing tells the user which password
 * is now set (production, 2026-10-16).
 *
 * So when the outcome is ambiguous, ask Cognito: sign in with the password just
 * entered. If that works it was saved, and the user is simply signed in. If it does
 * not, either it never reached Cognito (retrying is safe) or an earlier attempt
 * saved a different one (the session is spent and the form can only fail).
 *
 * Amplify UI 5 has no service override for this step: its forceNewPassword actor
 * calls `Auth.completeNewPassword` on the shared Auth instance, so that is the
 * method wrapped here.
 */

type CompleteNewPassword = (
  user: any,
  password: string,
  requiredAttributes?: Record<string, string>,
  clientMetadata?: Record<string, string>,
) => Promise<any>;

type SignIn = (username: string, password: string) => Promise<any>;

export type FirstLoginRecoveryOutcome = 'recovered' | 'already_changed';
export type FirstLoginErrorCode = 'NETWORK_ERROR' | 'SESSION_SPENT';

export const PASSWORD_ALREADY_SAVED_MESSAGE =
  'Your new password was already saved on an earlier attempt, ' +
  'so this form can no longer be used. ' +
  'Go back to Sign In and sign in with the new password you entered first. ' +
  'If you do not remember it, use Forgot your password.';

function errorCode(error: unknown): string {
  const e = error as { code?: string; name?: string } | undefined;
  return e?.code ?? e?.name ?? '';
}

export function isLostResponse(error: unknown): boolean {
  return errorCode(error) === 'NetworkError';
}

export function isSpentSession(error: unknown): boolean {
  return (
    errorCode(error) === 'NotAuthorizedException' &&
    /session can only be used once/i.test((error as Error).message ?? '')
  );
}

function usernameOf(user: any): string {
  return user?.challengeParam?.userAttributes?.email ?? user.getUsername();
}

export function withLostResponseRecovery(
  completeNewPassword: CompleteNewPassword,
  signIn: SignIn,
  onRecovery: (outcome: FirstLoginRecoveryOutcome, code: FirstLoginErrorCode) => void = () =>
    undefined,
): CompleteNewPassword {
  return async (user, password, requiredAttributes, clientMetadata) => {
    try {
      return await completeNewPassword(user, password, requiredAttributes, clientMetadata);
    } catch (error) {
      const lost = isLostResponse(error);
      const spent = isSpentSession(error);
      if (!lost && !spent) {
        throw error;
      }
      const code: FirstLoginErrorCode = lost ? 'NETWORK_ERROR' : 'SESSION_SPENT';
      let signedIn;
      try {
        signedIn = await signIn(usernameOf(user), password);
      } catch {
        if (spent) {
          onRecovery('already_changed', code);
          throw new Error(PASSWORD_ALREADY_SAVED_MESSAGE);
        }
        throw error;
      }
      onRecovery('recovered', code);
      return signedIn;
    }
  };
}

export function installFirstLoginPasswordRecovery(
  auth: { completeNewPassword: CompleteNewPassword },
  signIn: SignIn,
  onRecovery?: (outcome: FirstLoginRecoveryOutcome, code: FirstLoginErrorCode) => void,
): void {
  auth.completeNewPassword = withLostResponseRecovery(
    auth.completeNewPassword.bind(auth),
    signIn,
    onRecovery,
  );
}
